import { api, API_BASE } from './api';
import { readWeightInfo } from '../utils/weight';

export const GUEST_CART_KEY = 'preinvoice_cart_v1';
export const GUEST_GENERATION_KEY = 'preinvoice_guest_generation_v1';
export const GUEST_ENVELOPE_KEY = 'preinvoice_guest_v2';
export const LEGACY_GUEST_ID = '00000000-0000-4000-8000-000000000001';
export const pendingKey = (userId) => `preinvoice_pending_v1:${userId}`;

export function readGuestCart() {
  try {
    const envelope = JSON.parse(localStorage.getItem(GUEST_ENVELOPE_KEY) || 'null');
    if (envelope && Array.isArray(envelope.items)) return envelope;
    const legacy = JSON.parse(localStorage.getItem(GUEST_CART_KEY) || '[]');
    return { id: localStorage.getItem(GUEST_GENERATION_KEY) || LEGACY_GUEST_ID, items: Array.isArray(legacy) ? legacy : [] };
  } catch { return { id: LEGACY_GUEST_ID, items: [] }; }
}

export function operationId() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function cartReference(item) {
  return {
    product_item_id: item.is_catalog_category ? null : Number(item.product_item_id || item.id),
    category_id: item.is_catalog_category ? Number(item.category_id || String(item.id).replace('category-', '')) : null,
    brand_id: item.brand_id ? Number(item.brand_id) : null,
  };
}
export const cartKey = (item) => `${item.product_item_id || `category-${item.category_id}`}:${item.brand_id || 'no-brand'}`;

export function optimisticItems(base, pending) {
  let items = base.map((item) => ({ ...item }));
  for (const operation of pending) {
    if (operation.type === 'clear') { items = []; continue; }
    const additions = operation.type === 'merge' ? operation.items : operation.type === 'add' ? [operation.item] : [];
    if (additions.length) {
      for (let index = 0; index < additions.length; index += 1) {
        const item = additions[index];
        const key = cartKey(item);
        const existing = items.find((row) => row.cart_id === key);
        if (existing) existing.quantity = Number(existing.quantity) + Number(item.quantity);
        else items.push({ ...(operation.type === 'merge' ? operation.previews?.[index] : operation.preview), ...item, id: item.product_item_id || `category-${item.category_id}`, is_catalog_category: !item.product_item_id, cart_id: key, quantity: Number(item.quantity) });
      }
    } else if (operation.item) {
      const key = cartKey(operation.item);
      if (operation.type === 'remove') items = items.filter((row) => row.cart_id !== key);
      else items = items.map((row) => row.cart_id === key ? { ...row, quantity: operation.item.quantity } : row);
    }
  }
  return items;
}

function prepareSnapshot(snapshot) {
  return { ...snapshot, items: (snapshot.items || []).map((item) => ({ ...item, ...readWeightInfo(item) })) };
}

export class CartSyncClient {
  constructor({ userId, token, onChange, onError, onExpired, onMerge }) {
    Object.assign(this, { userId, token, onChange, onError, onExpired, onMerge });
    this.base = { revision: 0, items: [] };
    this.pending = [];
    this.ready = false;
    this.stopped = false;
    this.sending = false;
    this.status = 'loading';
    this.abort = new AbortController();
    this.delay = 1000;
    this.streamDelay = 1000;
    this.failures = 0;
    try {
      const saved = JSON.parse(sessionStorage.getItem(pendingKey(userId)) || '[]');
      if (Array.isArray(saved)) this.pending = saved.filter((operation) => operation && typeof operation.id === 'string' && ['add', 'set', 'remove', 'clear', 'merge'].includes(operation.type) && (operation.type === 'clear' || (operation.type === 'merge' ? Array.isArray(operation.items) : operation.item && typeof operation.item === 'object')) && Date.parse(operation.created_at) > Date.now() - 7 * 86400000).slice(0, 200);
    } catch { /* A missing draft must not prevent access to the server cart. */ }
  }
  emit() {
    if (!this.stopped) this.onChange({ owner: this.userId, ready: this.ready, status: this.status, items: optimisticItems(this.base.items, this.pending), revision: this.base.revision });
  }
  persist() {
    try { sessionStorage.setItem(pendingKey(this.userId), JSON.stringify(this.pending)); } catch { /* Keep the live in-memory queue when storage is unavailable. */ }
  }
  accept(snapshot) {
    if (snapshot && Number(snapshot.revision) >= this.base.revision) this.base = prepareSnapshot(snapshot);
    this.emit();
  }
  async refresh(force = false) {
    const result = await api.getCart(this.token, this.abort.signal);
    if (!this.stopped) {
      if (!force && this.ready && this.pending.length) this.wantedRevision = Math.max(this.wantedRevision || 0, Number(result.data.revision));
      else this.accept(result.data);
    }
    return this.base;
  }
  async start() {
    try {
      await this.refresh(true);
      if (this.stopped) return;
      const guestSnapshot = readGuestCart();
      const guest = guestSnapshot.items;
      if (Array.isArray(guest) && guest.length && guest.length <= 200) {
        const id = guestSnapshot.id;
        const references = guest.map((item) => ({ ...cartReference(item), quantity: Number(item.quantity) || 1 }));
        const valid = references.every((item) => Number.isSafeInteger(item.product_item_id || item.category_id) && (item.product_item_id || item.category_id) > 0 && item.quantity > 0 && item.quantity <= 1000000);
        if (valid && !this.pending.some((operation) => operation.id === id)) {
          this.pending.unshift({ id, type: 'merge', created_at: new Date().toISOString(), items: references, previews: guest });
          this.persist();
          this.onMerge(guest);
        } else if (!valid) this.onError('برخی ردیف‌های سبد مهمان نامعتبرند؛ اطلاعات قبلی شما حفظ شده است.');
      } else if (guest.length > 200) {
        this.onError('سبد مهمان بیش از ۲۰۰ ردیف دارد و بدون تغییر حفظ شده است.');
      }
      this.ready = true;
      this.status = this.pending.length ? 'saving' : 'synced';
      this.persist();
      this.emit();
      this.pump();
      this.stream();
      this.poll = setInterval(() => this.refresh().catch(() => {}), 30000);
    } catch (error) {
      if (this.stopped) return;
      if (error.status === 401) return this.expired();
      this.status = 'offline'; this.emit();
      this.startRetry = setTimeout(() => this.start(), Math.min(this.delay *= 2, 15000));
    }
  }
  expired() { if (!this.stopped) { this.status = 'error'; this.emit(); this.onExpired(); this.stop(); } }
  enqueue(type, item, expectedQuantity) {
    if (this.stopped || this.pending.length >= 200) { this.onError('تغییر ذخیره نشد؛ اتصال و وضعیت سبد را بررسی کنید.'); return false; }
    const operation = { id: operationId(), type, created_at: new Date().toISOString() };
    if (type === 'clear') operation.base_revision = this.base.revision + this.pending.length;
    if (item) { operation.item = { ...cartReference(item), ...(type === 'remove' ? {} : { quantity: Number(item.quantity) }) }; operation.preview = item; }
    if (expectedQuantity !== undefined) operation.expected_quantity = Number(expectedQuantity);
    this.pending.push(operation);
    this.status = 'saving'; this.persist(); this.emit(); this.pump();
    return true;
  }
  async pump() {
    if (this.sending || !this.ready || this.stopped || !this.pending.length) return;
    this.sending = true;
    while (this.pending.length && !this.stopped) {
      const operation = this.pending[0];
      if (operation.base_revision === undefined) { operation.base_revision = this.base.revision; this.persist(); }
      const { preview, previews, ...request } = operation;
      try {
        const result = await api.applyCartOperation(this.token, request, this.abort.signal);
        if (this.stopped) break;
        this.pending.shift(); this.persist();
        this.accept(result.data);
        if (result.data.skipped) this.onError('بعضی کالاهای سبد مهمان دیگر قابل سفارش نبودند و منتقل نشدند.');
        this.delay = 1000;
      } catch (error) {
        if (this.stopped) break;
        if (error.status === 401) { this.expired(); break; }
        if (error.status >= 400 && error.status < 500 && error.status !== 429) {
          this.failures += 1;
          this.pending.shift(); this.persist();
          if (error.payload?.cart) this.accept(error.payload.cart);
          else await this.refresh().catch(() => {});
          this.onError(error.message || 'تغییر سبد ذخیره نشد.');
          continue;
        }
        this.status = 'offline'; this.emit();
        this.retry = setTimeout(() => { this.retry = null; this.pump(); }, error.status === 429 ? 60000 : Math.min(this.delay *= 2, 15000));
        break;
      }
    }
    this.sending = false;
    if (!this.pending.length && !this.stopped) {
      this.status = 'synced'; this.emit();
      if (this.wantedRevision > this.base.revision) this.refresh().catch(() => {});
    }
  }
  async stream() {
    if (this.stopped) return;
    let reader;
    try {
      const response = await fetch(`${API_BASE}/api/web/cart/events`, { headers: { Authorization: `Bearer ${this.token}` }, signal: this.abort.signal, cache: 'no-store' });
      if (response.status === 401) return this.expired();
      if (!response.ok || !response.body) throw new Error('stream unavailable');
      this.streamDelay = 1000;
      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (!this.stopped) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r/g, '');
        if (buffer.length > 16384) throw new Error('invalid event');
        let end;
        while ((end = buffer.indexOf('\n\n')) >= 0) {
          const event = buffer.slice(0, end); buffer = buffer.slice(end + 2);
          const data = event.split('\n').find((line) => line.startsWith('data:'));
          if (data) {
            const revision = Number(JSON.parse(data.slice(5)).revision);
            if (Number.isSafeInteger(revision) && revision > this.base.revision) {
              this.wantedRevision = Math.max(this.wantedRevision || 0, revision);
              if (!this.pending.length) this.refresh().catch(() => {});
            }
          }
        }
      }
    } catch { /* Reconnect with authentication; never put the token in the URL. */ }
    finally { await reader?.cancel().catch(() => {}); }
    if (!this.stopped) this.streamRetry = setTimeout(() => this.stream(), Math.min(this.streamDelay *= 2, 15000));
  }
  async flush() {
    const failures = this.failures;
    const deadline = Date.now() + 12000;
    this.pump();
    while (!this.stopped && (!this.ready || this.pending.length || this.sending) && Date.now() < deadline) await new Promise((resolve) => setTimeout(resolve, 80));
    if (this.stopped || !this.ready || this.pending.length || this.sending) throw new Error('سبد هنوز ذخیره نشده است؛ اتصال اینترنت را بررسی کنید.');
    if (this.failures !== failures) throw new Error('سبد تغییر کرده است؛ قبل از ثبت سفارش، اقلام و تعدادها را دوباره بررسی کنید.');
    return this.refresh();
  }
  items() { return optimisticItems(this.base.items, this.pending); }
  stop() {
    this.stopped = true;
    this.abort.abort();
    clearInterval(this.poll);
    for (const timer of [this.retry, this.streamRetry, this.startRetry]) clearTimeout(timer);
  }
}
